import { useEffect, useRef, useState } from "react";

import perfectCaseLogo from "../assets/logo/PClogo.svg";
import { getAccessRequestStates, submitAccessRequest } from "../api/accessRequests";
import SelectStateModal from "../Components/TreatmentPage/modals/state-modal/SelectStateModal";
import SvgDefs from "../Components/BP/modals/CaseProviderModals/SvgDefs";

import "./RequestAccessPage.css";

const fields = ["first_name", "last_name", "email", "state"];
const errorText = (value) => (Array.isArray(value) ? value.join(" ") : String(value || ""));

const RequestAccessPage = () => {
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [statesError, setStatesError] = useState(false);
  const [statesAttempt] = useState(0);
  const [showStatesModal, setShowStatesModal] = useState(false);
  const [values, setValues] = useState({ first_name: "", last_name: "", email: "", state: "" });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const pending = useRef(false);
  const formRef = useRef(null);
  const successRef = useRef(null);
  const selectedState = states.find((state) => String(state.id) === values.state);

  useEffect(() => {
    document.title = "PerfectCase | Practice Management Perfected";
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setStatesLoading(true);
    setStatesError(false);
    getAccessRequestStates(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setStates(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setStatesError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setStatesLoading(false);
      });
    return () => controller.abort();
  }, [statesAttempt]);

  useEffect(() => {
    if (submitted) successRef.current?.focus();
  }, [submitted]);

  const updateValue = (event) => {
    const { name, value } = event.target;
    setValues((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (pending.current || statesLoading || statesError) return;
    const nextErrors = {};
    if (!values.first_name.trim()) nextErrors.first_name = "Enter your first name.";
    if (!values.last_name.trim()) nextErrors.last_name = "Enter your last name.";
    if (!selectedState) nextErrors.state = "Choose your state.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      formRef.current?.elements.namedItem(fields.find((field) => nextErrors[field]))?.focus();
      return;
    }

    pending.current = true;
    setSubmitting(true);
    setErrors({});
    setSubmitError("");
    try {
      await submitAccessRequest({
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        email: values.email.trim(),
        state: selectedState.id,
      });
      setSubmitted(true);
    } catch (error) {
      const fieldErrors = Object.fromEntries(
        fields
          .filter((field) => error.data?.[field])
          .map((field) => [field, errorText(error.data[field])])
      );
      setErrors(fieldErrors);
      if (Object.keys(fieldErrors).length) {
        formRef.current?.elements.namedItem(fields.find((field) => fieldErrors[field]))?.focus();
      } else {
        setSubmitError(
          errorText(error.data?.detail || error.data?.non_field_errors || error.data?.message) ||
            "We couldn’t send your request. Please try again."
        );
      }
    } finally {
      pending.current = false;
      setSubmitting(false);
    }
  };

  const input = (name, label, placeholder, autoComplete, type = "text") => (
    <div className="access-landing__field">
      <label className="access-landing__label" htmlFor={`access-${name}`}>
        {label}
      </label>
      <input
        id={`access-${name}`}
        name={name}
        type={type}
        spellCheck={type === "email" ? false : undefined}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required
        value={values[name]}
        onChange={updateValue}
        readOnly={submitting}
        aria-invalid={Boolean(errors[name])}
        aria-describedby={errors[name] ? `access-${name}-error` : undefined}
      />
      {errors[name] && (
        <p className="access-landing__error" id={`access-${name}-error`}>
          {errors[name]}
        </p>
      )}
    </div>
  );

  return (
    <div className="access-landing">
      <SvgDefs />
      <div className="access-landing__watermarks" aria-hidden="true">
        {Array.from({ length: 12 }, (_, index) => (
          <span key={index}>
            Practice
            <br />
            Management
            <br />
            Perfected
          </span>
        ))}
      </div>
      <main className="access-landing__main">
        <img
          className="access-landing__logo"
          src={perfectCaseLogo}
          alt="PerfectCase — Practice Management Perfected"
          width="366"
          height="120"
        />
        <h1>
          Run your entire personal-
          <br className="access-landing__desktop-break" />
          injury practice from one place.
        </h1>
        <p className="access-landing__description">
          PerfectCase brings intake, statute deadlines, medical and injury tracking, documents,
          calendaring, and settlement into a single dense, fast workspace
          <br className="access-landing__desktop-break" /> — built for the way PI firms actually
          work.
        </p>

        {submitted ? (
          <div className="access-landing__success" role="status" tabIndex={-1} ref={successRef}>
            <h2>You’re on the list.</h2>
            <p>
              Thanks for your interest in PerfectCase. We’ll be in touch at{" "}
              <strong>{values.email.trim()}</strong>.
            </p>
          </div>
        ) : (
          <form
            className="access-landing__form"
            ref={formRef}
            onSubmit={handleSubmit}
            aria-label="Request access"
            aria-busy={submitting}
          >
            <div className="access-landing__names">
              {input("first_name", "First name", "First name", "given-name")}
              {input("last_name", "Last name", "Last name", "family-name")}
            </div>
            {input("email", "Email", "you@yourfirm.com", "email", "email")}
            <div className="access-landing__actions">
              <div className="access-landing__field">
                <label className="access-landing__label" htmlFor="access-state">
                  State
                </label>
                <button
                  type="button"
                  id="access-state"
                  name="state"
                  className="access-landing__state-picker"
                  value={values.state}
                  onClick={() => setShowStatesModal(true)}
                  disabled={statesLoading || statesError || submitting}
                  aria-label={`State: ${selectedState?.name || "Select state"}`}
                  aria-haspopup="dialog"
                  aria-expanded={showStatesModal}
                  aria-invalid={Boolean(errors.state)}
                  aria-describedby={errors.state ? "access-state-error" : undefined}
                >
                  <span>{statesLoading ? "Loading states…" : selectedState?.name || "State"}</span>
                  <span className="access-landing__state-chevron" aria-hidden="true" />
                </button>
                {errors.state && (
                  <p className="access-landing__error" id="access-state-error">
                    {errors.state}
                  </p>
                )}
              </div>
              <button
                className="access-landing__submit"
                type="submit"
                disabled={statesLoading || statesError || submitting}
              >
                {submitting ? "Requesting…" : "Request access"}
              </button>
            </div>

            {submitError && (
              <p className="access-landing__error" role="alert">
                {submitError}
              </p>
            )}
            <p className="access-landing__privacy">No spam — just your invite.</p>
          </form>
        )}
      </main>
      <footer className="access-landing__footer">
        <span>© {new Date().getFullYear()} PerfectCase · Practice Management Perfected</span>
      </footer>
      {!submitted && (
        <SelectStateModal
          show={showStatesModal}
          handleClose={() => setShowStatesModal(false)}
          statesData={states}
          onChange={(state) => {
            setValues((previous) => ({ ...previous, state: String(state.id) }));
            setErrors((previous) => ({ ...previous, state: "" }));
            setSubmitError("");
          }}
        />
      )}
    </div>
  );
};

export default RequestAccessPage;
